import type { ReactNode } from 'react';
import { Building2Icon, LockIcon } from 'lucide-react';

import { VISIBILITY_NAME } from '@/features/workspaces/accessCopy';
import type {
  WorkspaceAccessView,
  WorkspaceVisibility,
} from '@/lib/api/generated/model';

/**
 * The person's role here, in the band beside the name. A member's only: "View only"
 * and "Organization admin" are said by the band's standing pill, as on every other
 * page of the workspace (spec 196 US1), and saying them twice in one band was noise.
 */
function roleWord(access: WorkspaceAccessView | null | undefined) {
  if (!access || access.as_org_admin) return null;
  if (access.role === 'owner') return 'Owner';
  if (access.role === 'member') return 'Member';
  return null;
}

export function BandChip({
  children,
  className,
  title,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  // Inside the band's `dark` scope, so the dark theme's measured pairings apply.
  return (
    <span
      title={title}
      className={`border-border text-foreground/90 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs whitespace-nowrap ${className ?? ''}`}
    >
      {children}
    </span>
  );
}

/**
 * The Settings band's pills (spec 227 FR-004), in order: the workspace's name,
 * who can see it, and this person's role. The name is a pill because the band's
 * title is "Settings" now, as Files' is "Files": the page is named for what it
 * is, and the workspace stands beside it. A long name is cut with an ellipsis and
 * kept in full on hover and for assistive technology.
 *
 * The row holds its height while the workspace is read, so the band does not
 * grow when the pills arrive.
 */
export function SettingsBandPills({
  workspace,
}: {
  workspace:
    | {
        name: string;
        visibility?: WorkspaceVisibility | null;
        access?: WorkspaceAccessView | null;
      }
    | undefined;
}) {
  if (!workspace) return <span aria-hidden="true" className="min-h-7" />;
  const visibility = workspace.visibility ?? 'private';
  const role = roleWord(workspace.access);
  const Icon = visibility === 'private' ? LockIcon : Building2Icon;
  return (
    <>
      <BandChip title={workspace.name}>
        <span className="max-w-[14rem] truncate sm:max-w-xs">
          {workspace.name}
        </span>
      </BandChip>
      <BandChip>
        <Icon aria-hidden="true" strokeWidth={1.5} className="size-3.5" />
        {VISIBILITY_NAME[visibility]}
      </BandChip>
      {role ? <BandChip>{role}</BandChip> : null}
    </>
  );
}
